import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";

const ResetPassword = () => {
  const { lang } = useLang();
  const { reset } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");

  const t = lang === "fr" ? {
    kicker: "Compte Dataleo",
    title: "Réinitialiser le mot de passe",
    intro: "Choisissez un nouveau mot de passe pour votre compte Dataleo.",
    password: "Nouveau mot de passe",
    confirm: "Confirmez le mot de passe",
    passwordHelp: "8 caractères minimum.",
    mismatch: "Les deux mots de passe ne correspondent pas.",
    missing: "Lien de réinitialisation invalide ou manquant.",
    submit: "Mettre à jour",
    sending: "Mise à jour…",
    ok: "Mot de passe mis à jour. Redirection vers la connexion…",
    back: "← Retour à la connexion",
  } : {
    kicker: "Dataleo account",
    title: "Reset password",
    intro: "Choose a new password for your Dataleo account.",
    password: "New password",
    confirm: "Confirm password",
    passwordHelp: "At least 8 characters.",
    mismatch: "The two passwords do not match.",
    missing: "Invalid or missing reset link.",
    submit: "Update password",
    sending: "Updating…",
    ok: "Password updated. Redirecting to sign in…",
    back: "← Back to sign in",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(""); setOk(false);
    if (!token) { setError(t.missing); return; }
    if (password !== confirm) { setError(t.mismatch); return; }
    setBusy(true);
    const res = await reset(token, password);
    if (res.ok) {
      setOk(true);
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } else {
      setError(res.error);
    }
    setBusy(false);
  };

  return (
    <div data-testid="reset-password-page">
      <SeoHead lang={lang} path="/reset-password" title={t.title} description={t.intro} noindex />
      <PageHero
        kicker={t.kicker}
        title={t.title}
        description={t.intro}
        accent="yellow"
        compact
        testId="reset-hero"
      />
      <div className="dl-container py-10 max-w-md">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.password} *</span>
          <input
            data-testid="reset-password-input"
            type="password"
            required
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
          <span className="text-[11px] text-[#9ca3af]">{t.passwordHelp}</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.confirm} *</span>
          <input
            data-testid="reset-password-confirm"
            type="password"
            required
            autoComplete="new-password"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
        <button
          type="submit"
          disabled={busy || ok}
          data-testid="reset-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-[#0f172a] disabled:opacity-60 text-white px-5 py-3 text-sm font-bold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} {busy ? t.sending : t.submit}
        </button>
        {ok && (
          <div data-testid="reset-ok" className="inline-flex items-center gap-2 text-sm text-[#00A878]">
            <CheckCircle2 size={16} /> {t.ok}
          </div>
        )}
        {error && (
          <div data-testid="reset-error" className="inline-flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
      </form>
      <div className="mt-6 text-sm">
        <Link to="/login" className="text-[#4B5563] hover:text-[#00A878]">{t.back}</Link>
      </div>
      </div>
    </div>
  );
};

export default ResetPassword;
